import { useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * NoCompanyEmptyState — uniform CTA block shown on company-scoped pages
 * (Job Positions, Applications, Dashboard sections, etc.) when the
 * authenticated user has no company yet.
 *
 * Routes the recruiter to /onboarding so they can create their first
 * company. The copy + iconography is intentionally identical across all
 * call sites so users get a consistent "you need a company first" signal
 * (Plan A — Phase 3 Task 3.2).
 */
export function NoCompanyEmptyState({
  /**
   * Optional override for the body copy. Defaults to
   * `emptyState.noCompany.body`. Pass a more contextual message when the
   * page can give the user useful framing (e.g. "Applications live inside
   * a company — create one to start receiving CVs").
   */
  body,
}: {
  body?: string;
}) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <Card className="border-border/50">
      <CardContent className="py-12 px-6 flex flex-col items-center text-center gap-4">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Building2 className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-1.5 max-w-md">
          <h3 className="text-base font-semibold">
            {t('emptyState.noCompany.title')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {body ?? t('emptyState.noCompany.body')}
          </p>
        </div>
        <Button onClick={() => navigate('/onboarding')}>
          {t('emptyState.noCompany.cta')}
        </Button>
      </CardContent>
    </Card>
  );
}
